import React, { useState } from 'react';
import { Save, Edit, Trash2, Search, Plus, X } from 'lucide-react';
import { useMagazzini } from '../hooks/useSupabase';

const Magazzini = () => {
  const { magazzini, loading, error, addMagazzino, updateMagazzino, deleteMagazzino } = useMagazzini();
  
  const [nuovoMagazzino, setNuovoMagazzino] = useState({
    id: '',
    nome: ''
  });
  
  const [modalitaModifica, setModalitaModifica] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const aggiungiMagazzino = async () => {
    if (!nuovoMagazzino.nome) {
      alert('Il nome del magazzino è obbligatorio');
      return;
    }
    
    try {
      if (modalitaModifica) {
        await updateMagazzino(nuovoMagazzino);
        setModalitaModifica(false);
      } else {
        await addMagazzino(nuovoMagazzino);
      }
      
      // Reset form
      setNuovoMagazzino({
        id: '',
        nome: ''
      });
      
      // Nascondi il form dopo il salvataggio
      setShowForm(false);
      // Pulisci eventuali messaggi di errore
      setErrorMessage('');
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  };
  
  const eliminaMagazzino = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo magazzino?')) return;
    
    try {
      await deleteMagazzino(id);
      // Pulisci eventuali messaggi di errore in caso di successo
      setErrorMessage('');
    } catch (err) {
      if (err.message.includes('violates foreign key constraint')) {
        alert('Impossibile eliminare il magazzino perché è utilizzato in uno o più viaggi.');
        // Non mostrare l'errore tecnico nella UI
        setErrorMessage(''); 
      } else {
        alert(`Errore: ${err.message}`);
        setErrorMessage('');
      }
    }
  };
  
  const modificaMagazzino = (magazzino) => {
    setNuovoMagazzino(magazzino);
    setModalitaModifica(true);
    setShowForm(true);
  };
  
  const nuovoMagazzinoClick = () => {
    setNuovoMagazzino({
      id: '',
      nome: ''
    });
    setModalitaModifica(false);
    setShowForm(true);
  };
  
  // Filtra i magazzini in base al termine di ricerca
  const magazziniFiltrati = magazzini.filter(magazzino => 
    magazzino.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="text-center py-8">Caricamento in corso...</div>;
  
  // Ignoriamo specificamente gli errori di foreign key constraint che abbiamo già gestito
  if (error && !error.includes('violates foreign key constraint')) {
    return <div className="text-center py-8 text-red-600">Errore: {error}</div>;
  }
  
  if (errorMessage) return <div className="text-center py-8 text-red-600">{errorMessage}</div>;

  return (
    <>
      {/* Container per la riga con entrambi i box */}
      <div className="flex gap-6 mb-6">
        {/* Pulsante Nuovo Magazzino - Box Separato */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Gestione Magazzini</h2>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={nuovoMagazzinoClick}
          >
            <Plus size={20} className="mr-2" />
            Nuovo Magazzino
          </button>
        </div>
        
        {/* Barra di ricerca - Box Separato */}
        <div className="bg-white p-6 rounded-lg shadow-md flex-grow">
          <h2 className="text-xl font-semibold mb-4">Cerca Magazzini</h2>
          <div className="flex items-center relative w-full">
            <Search size={20} className="absolute left-3 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca magazzini..."
              className="pl-10 p-2 border rounded w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Form Anagrafica Magazzini (mostrato solo quando necessario) */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{modalitaModifica ? 'Modifica Magazzino' : 'Nuovo Magazzino'}</h2>
            <button 
              onClick={() => setShowForm(false)}
              className="text-gray-500 hover:text-gray-700"
              title="Chiudi"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-1">Nome Magazzino *</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoMagazzino.nome}
                onChange={e => setNuovoMagazzino({...nuovoMagazzino, nome: e.target.value})}
                required
              />
            </div>
          </div>
          
          <button 
            className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={aggiungiMagazzino}
          >
            <Save size={20} className="mr-2" />
            {modalitaModifica ? 'Aggiorna Magazzino' : 'Salva Magazzino'}
          </button>
          
          {modalitaModifica && (
            <button 
              className="bg-gray-500 text-white px-4 py-2 rounded ml-2 inline-flex items-center"
              onClick={() => {
                setModalitaModifica(false);
                setNuovoMagazzino({
                  id: '',
                  nome: ''
                });
              }}
            >
              Annulla
            </button>
          )}
        </div>
      )}
      
      {/* Lista Magazzini */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Lista Magazzini</h2>
        
        {magazziniFiltrati.length === 0 ? (
          <p className="text-gray-500">
            {searchTerm ? 'Nessun magazzino trovato per la ricerca effettuata' : 'Nessun magazzino registrato'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-left">Nome Magazzino</th>
                  <th className="py-2 px-3 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {magazziniFiltrati.map(magazzino => (
                  <tr key={magazzino.id} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-3">{magazzino.nome}</td>
                    <td className="py-2 px-3 text-center">
                      <button 
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        onClick={() => modificaMagazzino(magazzino)}
                        title="Modifica"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-800"
                        onClick={() => eliminaMagazzino(magazzino.id)}
                        title="Elimina"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Magazzini;